import React, { useRef, useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShaderStyle, ShaderPreset, ThemeTokens } from '../theme/stylesRegistry';

// ============================================
// TYPES
// ============================================
interface ShaderBackgroundProps {
  style: ShaderStyle;
  preset: ShaderPreset;
  theme: ThemeTokens;
}

// ============================================
// PERFORMANCE CONFIG
// ============================================
// Reduce this for better performance (1.0 = native resolution, 0.5 = half resolution)
const DESKTOP_RESOLUTION_SCALE = 0.62;
const MOBILE_RESOLUTION_SCALE = 0.5;
const LOW_END_DEVICE_SCALE = 0.42;
// Debounce resize events (ms)
const RESIZE_DEBOUNCE = 300;

const getResolutionScale = () => {
  if (typeof window === 'undefined') return DESKTOP_RESOLUTION_SCALE;

  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  const lowCPU = typeof navigator !== 'undefined' && navigator.hardwareConcurrency <= 4;
  const lowMemory = typeof navigator !== 'undefined' && 'deviceMemory' in navigator
    ? ((navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8) <= 4
    : false;

  if (lowCPU || lowMemory) return LOW_END_DEVICE_SCALE;
  return isMobile ? MOBILE_RESOLUTION_SCALE : DESKTOP_RESOLUTION_SCALE;
};

// ============================================
// DEBOUNCED RESIZE OBSERVER HOOK
// ============================================
const useContainerSize = (ref: React.RefObject<HTMLDivElement>, resolutionScale: number) => {
  const [size, setSize] = useState({ width: 1280, height: 720 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Debounced size update
    const updateSize = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        const rect = element.getBoundingClientRect();
        // Use reduced resolution for better performance
        const scale = resolutionScale;
        setSize({
          width: Math.round(rect.width * scale),
          height: Math.round(rect.height * scale),
        });
      }, RESIZE_DEBOUNCE);
    };

    // Initial size (immediate, no debounce)
    const rect = element.getBoundingClientRect();
    setSize({
      width: Math.round(rect.width * resolutionScale),
      height: Math.round(rect.height * resolutionScale),
    });

    // ResizeObserver for responsive updates
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [ref, resolutionScale]);

  return size;
};

// ============================================
// SHADER BACKGROUND COMPONENT
// ============================================
const ShaderBackground: React.FC<ShaderBackgroundProps> = ({
  style,
  preset,
  theme,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const resolutionScale = useMemo(getResolutionScale, []);
  const size = useContainerSize(containerRef, resolutionScale);

  // Check for reduced motion preference
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  // Memoize shader rendering to prevent unnecessary re-renders
  const shaderElement = useMemo(() => {
    // If user prefers reduced motion, render a static gradient instead
    if (prefersReducedMotion) {
      return (
        <div
          className="w-full h-full"
          style={{
            background: theme.mood === 'light'
              ? 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)'
              : 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
          }}
        />
      );
    }

    return style.renderShader({
      width: size.width,
      height: size.height,
      presetProps: preset.props,
    });
  }, [style, preset.props, size.width, size.height, prefersReducedMotion, theme.mood]);

  // Generate a unique key for transitions
  const transitionKey = `${style.id}-${preset.id}`;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-0 overflow-hidden"
      style={{ isolation: 'isolate' }}
    >
      {/* Shader layer with fade transition */}
      <AnimatePresence mode="sync" initial={false}>
        <motion.div
          key={transitionKey}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="absolute inset-0"
          style={{
            // Scale up to fill container (shaders render at reduced res)
            transform: `scale(${1 / resolutionScale})`,
            transformOrigin: 'top left',
            width: `${size.width}px`,
            height: `${size.height}px`,
            willChange: 'opacity',
          }}
        >
          {shaderElement}
        </motion.div>
      </AnimatePresence>

      {/* Single overlay for readability (combined for performance) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: theme.mood === 'light'
            ? `linear-gradient(to bottom,
                rgba(255,255,255,${theme.background.overlayStrength * 0.3}) 0%,
                rgba(255,255,255,${theme.background.overlayStrength * 0.5}) 50%,
                rgba(255,255,255,${theme.background.overlayStrength * 0.3}) 100%)`
            : `linear-gradient(to bottom,
                rgba(0,0,0,${theme.background.overlayStrength * 0.4}) 0%,
                rgba(0,0,0,${theme.background.overlayStrength * 0.2}) 50%,
                rgba(0,0,0,${theme.background.overlayStrength * 0.5}) 100%)`,
        }}
      />
    </div>
  );
};

export default React.memo(ShaderBackground);
